import { capitalize } from 'lodash'
import React, { FC, useCallback, useEffect, useState } from 'react'
import 'react-h5-audio-player/lib/styles.css'
import { pdfjs } from 'react-pdf'
import { useDispatch } from 'react-redux'
import { RouteComponentProps, useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import { get } from 'src/apis/apiService'
import { setPageTitle, setPreviousPage } from 'src/ducks/onDemand'
import routes from 'src/routes'
import Amplitude from 'src/utils/amplitudeHelper'
import { ON_DEMAND_APIS } from '../../../apis/onDemandApi'
import { Loader } from '../../shared/Loader'
import { OralPresentation } from '../OralPresentation'
import { PosterPresentation } from '../PosterPresentation'
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js'

export interface AbstractType {
  id?: number
  title?: string
  submission_type?: 'poster' | 'oral'
  presenter_email?: string
  presenting_author_name?: string
  area_of_interest?: string
  audio_recording?: {
    url: string
  }
  poster?: {
    url: string
  }
  vimeo_video?: {
    vimeo_id: string
  }
  award_type?: string
}

const pageTitle: { [key in AbstractType['submission_type']]: string } = {
  poster: 'E-Poster Presentation',
  oral: 'Oral Presentation',
}

const submissionTypePage: { [key in AbstractType['submission_type']]: string } = {
  poster: routes.onDemandPoster,
  oral: routes.onDemandOral,
}

export const PresentationContainer: FC<RouteComponentProps> = ({ match }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const [abstract, setAbstract] = useState<AbstractType>({})
  const [hasRated, setHasRated] = useState(false)
  const [pageLoading, setPageLoading] = useState<boolean>(false)

  const abstractId = match.params['abstractId']

  useEffect(() => {
    dispatch(setPageTitle(pageTitle[abstract.submission_type]))
    if (abstract.award_type === 'best_basic_award') {
      dispatch(setPreviousPage(routes.onDemandBbs))
    } else {
      dispatch(setPreviousPage(submissionTypePage[abstract.submission_type] + '/' + abstract.area_of_interest))
    }
  }, [abstract.area_of_interest, abstract.award_type, abstract.submission_type, dispatch])

  const getAbstract = useCallback(() => {
    setPageLoading(true)
    get(
      ON_DEMAND_APIS.GET_ABSTRACT(abstractId),
      (result) => {
        setPageLoading(false)
        if (result.success) {
          const abstractResult: AbstractType = result.abstract
          setAbstract(abstractResult)
          setHasRated(result.rated)
          Amplitude.viewAbstractPresentation(abstractResult, capitalize(abstractResult.submission_type))
        } else {
          toast('This abstract does not exist', { toastId: 'abstract-not-exist' })
          history.push(routes.onDemand)
        }
      },
      (error) => {
        setPageLoading(false)
        toast('Unable to display this abstract. Please try again later.')
        history.push(routes.onDemand)
        console.error(error)
      }
    )
  }, [abstractId, history])

  useEffect(() => {
    getAbstract()
  }, [getAbstract])

  const renderAbstract = () => {
    if (abstract.submission_type === 'oral') {
      return <OralPresentation hasRated={hasRated} abstract={abstract} />
    }

    if (abstract.submission_type === 'poster') {
      return <PosterPresentation hasRated={hasRated} abstract={abstract} />
    }
  }

  return (
    <Loader fullPage loading={pageLoading}>
      {renderAbstract()}
    </Loader>
  )
}
